import 'dayjs/locale/lt';

import { Box, Card, CardContent } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldsConfigProvider } from '../../../contexts/useFieldsConfigDialog';
import { usePage } from '../../../contexts/usePageContext';
import { useAppDispatch } from '../../../redux/hooks';
import {
 resetHeaderTitle,
 setHeaderTitle,
} from '../../../redux/header/headerSlice';
import ColModal from '../ColModal';
import EditModal from '../EditModal';
import FilterMenu from '../FilterMenu';
import FilterSidebar from '../FilterSidebar';
import InfoSidebar from '../InfoSidebar';
import LimitModal from '../LimitModal';
import TranslationModal from '../TranslationModal';
import UserEditModal from '../UserEditModal';
import {
 FILTER_DEBOUNCE_MS,
 NO_MODAL_ID,
 PAGE_SIZE_OPTIONS,
} from './constants';
import useColumnPresets from './hooks/useColumnPresets';
import useGridConfig from './hooks/useGridConfig';
import useTableFetch from './hooks/useTableFetch';
import useTableInit from './hooks/useTableInit';
import { GRID_DEFAULT_LOCALE_TEXT } from './translation';
import { XTableProps } from './types';
import { handleFilterModelChange } from './utils';
import { useXTableContext, XTableProvider } from './XTableContext';

const XTableContent = (props: XTableProps) => {
 const { pageName } = usePage();
 const { t } = useTranslation();
 const dispatch = useAppDispatch();
 const {
  fetchData,
  fetchDataParams,
  isServerMode = false,
  defaultPageSize = 50,
  rowCount,
  rows,
  apiRef,
  table,
 } = props;

 const {
  modalState,
  setModalState,
  createModalOpen,
  setCreateModalOpen,
  colModalOpen,
  setColModalOpen,
  filterMenuState,
  setFilterMenuState,
  limitModalState,
  setLimitModalState,
  translationModalState,
  setTranslationModalState,
  currentId,
  setCurrentId,
  filterSidebarOpen,
  setFilterSidebarOpen,
 } = useXTableContext();

 const {
  columnsState,
  currentColumnsPreset,
  presetsColumns,
  structure,
  handleColumnResize,
  handleColumnOrderChange,
 } = useColumnPresets(pageName);

 const {
  isInit,
  filter,
  setFilter,
  sortModel,
  setSortModel,
  paginationModel,
  setPaginationModel,
  initialState,
  loading,
 } = useTableInit({
  pageName,
  apiRef,
  table,
  defaultPageSize,
  columnsState,
  presetsColumns,
 });

 const { wrapFetchData } = useTableFetch({
  fetchData,
  fetchDataParams,
  apiRef,
  structure,
  currentColumnsPreset,
  paginationModel,
  isInit,
  loading,
  filter,
  sortModel,
 });

 useEffect(() => {
  if (!apiRef.current) return;
  apiRef.current.setModalState = setModalState;
  apiRef.current.setCurrentId = setCurrentId;
  apiRef.current.setCreateModalOpen = setCreateModalOpen;
  apiRef.current.filterSidebarOpen = filterSidebarOpen;
  apiRef.current.setFilterSidebarOpen = setFilterSidebarOpen;
  apiRef.current.fetchData = wrapFetchData;
  apiRef.current.colModalOpen = colModalOpen;
  apiRef.current.setColModalOpen = setColModalOpen;
  apiRef.current.setLimitModalState = setLimitModalState;
  apiRef.current.setFilterMenuState = setFilterMenuState;
  apiRef.current.setTranslationModalState = setTranslationModalState;
 }, [
  apiRef,
  setModalState,
  setCurrentId,
  setCreateModalOpen,
  filterSidebarOpen,
  setFilterSidebarOpen,
  wrapFetchData,
  colModalOpen,
  setColModalOpen,
  setLimitModalState,
  setFilterMenuState,
  setTranslationModalState,
 ]);

 const {
  groupingColDef,
  processedRows,
  allColumns,
  gridSx,
  slots,
  gridMode,
  handleRowGroupingModelChange,
  getRowClassName,
 } = useGridConfig({
  apiRef,
  columnsState,
  currentColumnsPreset,
  rows,
  isServerMode,
  onRowClick: props.onRowClick,
  onCellClick: props.onCellClick,
  propSlots: props.slots,
 });

 useEffect(() => {
  const title = `${t(`entities.${pageName}`)} (${rowCount})`;
  dispatch(setHeaderTitle(title));
  return () => {
   dispatch(resetHeaderTitle());
  };
 }, [dispatch, pageName, rowCount, t]);

 if (!initialState) {
  return <>loading state</>;
 }

 return (
  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="lt">
   <FieldsConfigProvider>
    {limitModalState?.anchorElement && (
     <LimitModal data={limitModalState} setOpen={setLimitModalState} />
    )}
    {translationModalState?.anchorElement && (
     <TranslationModal
      data={translationModalState}
      setOpen={setTranslationModalState}
      tableName={table}
     />
    )}
    {filterMenuState?.anchorElement && (
     <FilterMenu
      filterMenuState={filterMenuState}
      setOpen={setFilterMenuState}
      apiRef={apiRef}
     />
    )}

    <Box
     sx={{
      display: 'flex',
      height: { xs: '100%', md: `100%` },
     }}
    >
     <Card
      style={{
       width: '100%',
       flex: 1,
       transition: 'all 0.2s ease-in-out',
      }}
     >
      <CardContent
       sx={{
        display: 'flex',
        p: '0 !important',
        width: '100%',
        height: '100%',
       }}
      >
       <DataGridPremium
        className={`${currentColumnsPreset?.isPro ? 'MuiDataGrid--isPro' : ''}`}
        disableRowSelectionOnClick
        checkboxSelection
        groupingColDef={groupingColDef}
        slots={slots}
        slotProps={{
         loadingOverlay: {
          variant: 'linear-progress',
          noRowsVariant: 'skeleton',
         },
        }}
        initialState={initialState}
        disableVirtualization
        rows={processedRows}
        columns={allColumns}
        onColumnResize={handleColumnResize}
        onColumnOrderChange={handleColumnOrderChange}
        onRowGroupingModelChange={handleRowGroupingModelChange}
        loading={!!loading}
        apiRef={apiRef}
        rowCount={rowCount || 0}
        pagination
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        filterModel={filter}
        filterMode={gridMode}
        filterDebounceMs={FILTER_DEBOUNCE_MS}
        onFilterModelChange={(model, details) => {
         handleFilterModelChange(model, details, setFilter);
        }}
        sortingMode={gridMode}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
        paginationMode={gridMode}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowHeight={() => 'auto'}
        density={currentColumnsPreset?.isPro ? 'compact' : 'standard'}
        sx={gridSx}
        getRowClassName={getRowClassName}
        localeText={GRID_DEFAULT_LOCALE_TEXT}
       />
      </CardContent>
     </Card>
     <InfoSidebar
      apiRef={apiRef}
      currentId={currentId}
      setCurrentId={setCurrentId}
     />
     <FilterSidebar
      apiRef={apiRef}
      isOpen={filterSidebarOpen}
      handleClose={setFilterSidebarOpen}
     />
    </Box>

    {colModalOpen && (
     <ColModal
      isOpen={colModalOpen}
      handleClose={setColModalOpen}
      apiRef={apiRef}
     />
    )}

    {pageName === 'users' ? (
     <UserEditModal
      setCreateModalOpen={setCreateModalOpen}
      isOpen={createModalOpen || modalState?.id !== NO_MODAL_ID}
      modalState={modalState}
      setModalState={setModalState}
      apiRef={apiRef}
     />
    ) : (
     <EditModal
      setCreateModalOpen={setCreateModalOpen}
      isOpen={createModalOpen || modalState?.id !== NO_MODAL_ID}
      modalState={modalState}
      setModalState={setModalState}
      apiRef={apiRef}
     />
    )}
   </FieldsConfigProvider>
  </LocalizationProvider>
 );
};

const XTable = memo((props: XTableProps) => (
 <XTableProvider>
  <XTableContent {...props} />
 </XTableProvider>
));

XTable.displayName = 'XTable';

export default XTable;

